import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageRectComponent } from "../image-rect-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { SelectionBottomLeftControlComponent } from "./selection-bottom-left-control-component";
import { SelectionBottomRightControlComponent } from "./selection-bottom-right-control-component";
import { SelectionRotationComponent } from "./selection-rotation-component";
import { SelectionTopLeftControlComponent } from "./selection-top-left-control-component";
import { SelectionTopRightControlComponent } from "./selection-top-right-control-component";
import CanvasLineUnitComponent from "../canvas-resize-component/canvas-line-unit-component";

export class SelectionComponent extends Webgl2Component {
  private _rectDownTranslation = Vec2.zero();
  private _mouseDownPosition = Vec2.zero();
  private _centerHorizontalLineComponents: Nullable<Webgl2Component>;
  private _centerVerticalLineComponents: Nullable<Webgl2Component>;
  private _edgeHorizontalLineComponents: Nullable<Webgl2Component>;
  private _edgeVerticalLineComponents: Nullable<Webgl2Component>;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    const rotationControl = new SelectionRotationComponent(this._renderer, this);
    rotationControl.style.top = "100%";
    rotationControl.style.left = "50%";
    rotationControl.style.translateX = "-50%";
    rotationControl.style.translateY = "0%&16px";
    this.appendChild(rotationControl);

    const topLeftControl = new SelectionTopLeftControlComponent(this._renderer, this);
    topLeftControl.style.top = "0";
    topLeftControl.style.left = "0";
    topLeftControl.style.translateX = "-50%";
    topLeftControl.style.translateY = "-50%";
    this.appendChild(topLeftControl);

    const topRightControl = new SelectionTopRightControlComponent(this._renderer, this);
    topRightControl.style.top = "0";
    topRightControl.style.left = "100%";
    topRightControl.style.translateX = "-50%";
    topRightControl.style.translateY = "-50%";
    this.appendChild(topRightControl);

    const bottomLeftControl = new SelectionBottomLeftControlComponent(this._renderer, this);
    bottomLeftControl.style.top = "100%";
    bottomLeftControl.style.left = "0";
    bottomLeftControl.style.translateX = "-50%";
    bottomLeftControl.style.translateY = "-50%";
    this.appendChild(bottomLeftControl);

    const bottomRightControl = new SelectionBottomRightControlComponent(this._renderer, this);
    bottomRightControl.style.top = "100%";
    bottomRightControl.style.left = "100%";
    bottomRightControl.style.translateX = "-50%";
    bottomRightControl.style.translateY = "-50%";
    this.appendChild(bottomRightControl);

    // TODO:?
    // NOTE: This is crutch to update component on scale change, since component doesn't have
    // relation to camera change
    this._renderer.camera.events.on("scale", (newScale: number) => {
      const value = 1 / newScale;
      this.forEachChildren((child) => {
        child.style.scaleX = value * window.devicePixelRatio;
        child.style.scaleY = value * window.devicePixelRatio;
      });
    });
  }

  public copyComponentTransform(component: Nullable<Webgl2Component<any, any>>) {
    if (!component) return;
    this.transform.copy(component.transform);
  }

  public onMouseDown(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    this._mouseDownPosition = this._renderer.camera.getGlobalMousePixelPosition(e);
    this._rectDownTranslation = this._selectedComponent.transform.translation.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    this._renderer.canvas.style.cursor = "move";
    const currentMousePos = this._renderer.camera.getGlobalMousePixelPosition(e);
    const diff = currentMousePos.sub(this._mouseDownPosition);
    const newTranslation = diff.add(this._rectDownTranslation);
    if (this._renderer.isLogoWatermarkEdit) {
      const componentOriginCenterPosition = this.calculateComponentCenterPosition(newTranslation);
      if (!this.isInCanvas(componentOriginCenterPosition)) {
        this.toggleChildrenDisplay("initial");
        this._renderer.render();
        return;
      } else {
        this.toggleChildrenDisplay("none");
      }
      if (this.handleCenterAnEdgeAlignment(componentOriginCenterPosition, newTranslation)) return;
    }
    this.transform.setTranslation(newTranslation);
    this._selectedComponent?.transform.setTranslation(newTranslation);
  }

  public onMouseUp(e: MouseEvent | TouchEvent) {
    this._renderer.canvas.style.cursor = "default";
    if (this._renderer.isLogoWatermarkEdit) {
      this._children.forEach((child) => (child.style.display = "initial"));
    }
    const currentMousePos = this._renderer.camera.getGlobalMousePixelPosition(e);
    const diff = currentMousePos.sub(this._mouseDownPosition);
    if (diff.isZero() || !this._selectedComponent) return;
    const component = this._selectedComponent;
    const newTranslation = diff.add(this._rectDownTranslation);
    const prevTranslation = this._rectDownTranslation.clone();

    this._renderer.commands.execute(
      new CommandAction(
        "move",
        () => {
          if (this._renderer.isLogoWatermarkEdit) {
            const componentOriginCenterPosition =
              this.calculateComponentCenterPosition(newTranslation);
            if (!this.isInCanvas(componentOriginCenterPosition)) return;
            if (this.handleCenterAnEdgeAlignment(componentOriginCenterPosition, newTranslation)) {
              this.removeAlignmentLines();
              return;
            }
          }
          this._renderer.selectComponent(component);
          this.transform.setTranslation(newTranslation);
          component.transform.setTranslation(newTranslation);
        },
        () => {
          this._renderer.selectComponent(component);
          this.transform.setTranslation(prevTranslation);
          component.transform.setTranslation(prevTranslation);
        },
      ),
    );
  }

  private calculateComponentCenterPosition(position: Vec2): Vec2 {
    if (!this._selectedComponent) return position;
    const matrix = this._selectedComponent.getModelMatrix();
    const newArray = new Float32Array(matrix);
    newArray[6] = position.x;
    newArray[7] = position.y;
    return Vec2.half().transformMat3(newArray);
  }

  private isWithinThreshold(value: number, target: number, threshold = 20): boolean {
    return Math.abs(value - target) <= threshold;
  }

  private getAtCenterX(componentPosition: Vec2): Nullable<number> {
    const canvasWidth = this._renderer.canvasSize.x;
    const canvasHeight = this._renderer.canvasSize.y;
    const centerX = canvasWidth / 2;

    if (this.isWithinThreshold(centerX, componentPosition.x)) {
      if (!this._centerHorizontalLineComponents) {
        this._centerHorizontalLineComponents = new CanvasLineUnitComponent(
          new Vec2(2, canvasHeight),
        );
        this._renderer.addComponent(this._centerHorizontalLineComponents);
        this._centerHorizontalLineComponents.transform.setTranslation(centerX - 2 / 2, 0);
      }
      return centerX;
    } else {
      this.removeCenterHorizontalLineComponent();
      return null;
    }
  }

  private getAtCenterY(componentPosition: Vec2): Nullable<number> {
    const canvasWidth = this._renderer.canvasSize.x;
    const canvasHeight = this._renderer.canvasSize.y;
    const centerY = canvasHeight / 2;

    if (this.isWithinThreshold(centerY, componentPosition.y)) {
      if (!this._centerVerticalLineComponents) {
        this._centerVerticalLineComponents = new CanvasLineUnitComponent(new Vec2(canvasWidth, 2));
        this._renderer.addComponent(this._centerVerticalLineComponents);
        this._centerVerticalLineComponents.transform.setTranslation(0, centerY - 2 / 2);
      }
      return centerY;
    } else {
      this.removeCenterVerticalLineComponent();
      return null;
    }
  }

  private isMatrixAtEdge(
    centerX: number,
    centerY: number,
    containerWidth: number,
    containerHeight: number,
    threshold = 20,
  ) {
    const matrixWidth = this.transform.size.x;
    const matrixHeight = this.transform.size.y;
    const angle = this.transform.rotation.value;
    const radians = (angle * Math.PI) / 180;
    const halfWidth = matrixWidth / 2;
    const halfHeight = matrixHeight / 2;
    const corners = [
      { x: halfWidth, y: halfHeight },
      { x: halfWidth, y: -halfHeight },
      { x: -halfWidth, y: halfHeight },
      { x: -halfWidth, y: -halfHeight },
    ];

    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const rotatedCorners = corners.map((corner) => ({
      x: centerX + (corner.x * cos - corner.y * sin),
      y: centerY + (corner.x * sin + corner.y * cos),
    }));

    const isEdge = {
      top: false,
      bottom: false,
      left: false,
      right: false,
      corner: { x: rotatedCorners[2].x, y: rotatedCorners[1].y },
    };
    const xMax = Math.max(...rotatedCorners.map((corner) => corner.x));
    const xMin = Math.min(...rotatedCorners.map((corner) => corner.x));
    const yMax = Math.max(...rotatedCorners.map((corner) => corner.y));
    const yMin = Math.min(...rotatedCorners.map((corner) => corner.y));

    if (this.isWithinThreshold(xMax, containerWidth, threshold)) {
      isEdge.right = true;
      isEdge.corner.x = isEdge.corner.x + (containerWidth - xMax);
    } else if (this.isWithinThreshold(xMin, 0, threshold)) {
      isEdge.left = true;
      isEdge.corner.x = isEdge.corner.x + -xMin;
    }

    if (this.isWithinThreshold(yMin, 0, threshold)) {
      isEdge.top = true;
      isEdge.corner.y = isEdge.corner.y + -yMin;
    } else if (this.isWithinThreshold(yMax, containerHeight, threshold)) {
      isEdge.bottom = true;
      isEdge.corner.y = isEdge.corner.y + (containerHeight - yMax);
    }

    return isEdge;
  }

  private getAtEdgeY(componentPosition: Vec2): Nullable<number> {
    const canvasWidth = this._renderer.canvasSize.x;
    const canvasHeight = this._renderer.canvasSize.y;
    const isEdge = this.isMatrixAtEdge(
      componentPosition.x,
      componentPosition.y,
      canvasWidth,
      canvasHeight,
    );

    if (isEdge.top || isEdge.bottom) {
      if (!this._edgeHorizontalLineComponents) {
        this._edgeHorizontalLineComponents = new CanvasLineUnitComponent(new Vec2(canvasWidth, 2));
        this._renderer.addComponent(this._edgeHorizontalLineComponents);
        this._edgeHorizontalLineComponents.transform.setTranslation(
          0,
          isEdge.top ? 0 : canvasHeight - 2,
        );
      }
      return isEdge.corner.y;
    } else {
      this.removeEdgeHorizontalLineComponent();
      return null;
    }
  }

  private getAtEdgeX(componentPosition: Vec2): Nullable<number> {
    const canvasWidth = this._renderer.canvasSize.x;
    const canvasHeight = this._renderer.canvasSize.y;
    const isEdge = this.isMatrixAtEdge(
      componentPosition.x,
      componentPosition.y,
      canvasWidth,
      canvasHeight,
    );

    if (isEdge.left || isEdge.right) {
      if (!this._edgeVerticalLineComponents) {
        this._edgeVerticalLineComponents = new CanvasLineUnitComponent(new Vec2(2, canvasHeight));
        this._renderer.addComponent(this._edgeVerticalLineComponents);
        this._edgeVerticalLineComponents.transform.setTranslation(
          isEdge.left ? 0 : canvasWidth - 2,
          0,
        );
      }
      return isEdge.corner.x;
    } else {
      this.removeEdgeVerticalLineComponent();
      return null;
    }
  }

  private handleCenterAnEdgeAlignment(componentPosition: Vec2, newTranslation: Vec2): boolean {
    const edgeY = this.getAtEdgeY(componentPosition);
    const edgeX = this.getAtEdgeX(componentPosition);
    const centerX = this.getAtCenterX(componentPosition);
    const centerY = this.getAtCenterY(componentPosition);

    const x = centerX ? centerX - componentPosition.x + newTranslation.x : newTranslation.x;
    const y = centerY ? centerY - componentPosition.y + newTranslation.y : newTranslation.y;
    const canExecute = centerX != null || centerY != null || edgeY != null || edgeX != null;
    if (canExecute) {
      this.updateTransform(new Vec2(edgeX != null ? edgeX : x, edgeY != null ? edgeY : y));
    }
    return canExecute;
  }

  private updateTransform(newTranslation: Vec2) {
    this.transform.setTranslation(newTranslation);
    this._selectedComponent?.transform.setTranslation(newTranslation);
  }

  private isInCanvas(componentOriginCenterPosition: Vec2): boolean {
    if (!this._selectedComponent) return false;
    const canvasWidth = this._renderer.canvasSize.x;
    const canvasHeight = this._renderer.canvasSize.y;
    const rotatedDimensions = this.transform.rotatedDimensions;
    const limitX = rotatedDimensions.width / 6;
    const limitY = rotatedDimensions.height / 6;
    const xWithinBounds =
      componentOriginCenterPosition.x > -limitX &&
      componentOriginCenterPosition.x < canvasWidth + limitX;
    const yWithinBounds =
      componentOriginCenterPosition.y > -limitY &&
      componentOriginCenterPosition.y < canvasHeight + limitY;
    return xWithinBounds && yWithinBounds;
  }

  private toggleChildrenDisplay(display: "initial" | "none") {
    this._children.forEach((child) => (child.style.display = display));
  }

  private removeCenterHorizontalLineComponent() {
    if (this._centerHorizontalLineComponents) {
      this._renderer.removeComponent(this._centerHorizontalLineComponents);
      this._centerHorizontalLineComponents = null;
    }
  }

  private removeCenterVerticalLineComponent() {
    if (this._centerVerticalLineComponents) {
      this._renderer.removeComponent(this._centerVerticalLineComponents);
      this._centerVerticalLineComponents = null;
    }
  }

  private removeEdgeHorizontalLineComponent() {
    if (this._edgeHorizontalLineComponents) {
      this._renderer.removeComponent(this._edgeHorizontalLineComponents);
      this._edgeHorizontalLineComponents = null;
    }
  }

  private removeEdgeVerticalLineComponent() {
    if (this._edgeVerticalLineComponents) {
      this._renderer.removeComponent(this._edgeVerticalLineComponents);
      this._edgeVerticalLineComponents = null;
    }
  }

  private removeAlignmentLines() {
    this.removeCenterHorizontalLineComponent();
    this.removeCenterVerticalLineComponent();
    this.removeEdgeHorizontalLineComponent();
    this.removeEdgeVerticalLineComponent();
  }

  public getRenderData() {
    if (!this._renderer.hasSelectedComponent) return;

    const material = Webgl2.materials.selectionGrid;
    material.setUniforms({
      u_size: this.transform.size.getAbs().getArray(),
      u_showGrid: false,
    });
    return { material };
  }
}

export default SelectionComponent;
